// src/pages/TeamDashboard.jsx
// =============================================================================
// DASHBOARD ÉQUIPE ARTKIV
// =============================================================================
// Vue d'ensemble pour les membres de l'équipe (role = "team")
// Gère : demandes partenaires, tickets support, demandes assurance,
// partenaires (contrats & événements facturables), newsletters,
// galeries, services curatoriaux, outils rapides.
// =============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { coinsForProfile, COIN_BY_KEY, hasCoin } from "../config/teamCoins";

import SharedDashboard from "../components/SharedDashboard";
import CuratorialRequestsSection from "../components/team/CuratorialRequestsSection";
import ValuationRequestsSection from "../components/team/ValuationRequestsSection";

import {
  ClipboardList,
  UserCircle2,
  Mail,
  Search,
  RefreshCw,
  ExternalLink,
  Users,
  FileText,
  PlusCircle,
} from "lucide-react";

const localeFor = (language) => {
  const lang = String(language || "fr").toLowerCase();
  if (lang.startsWith("de")) return "de-DE";
  if (lang.startsWith("en")) return "en-GB";
  return "fr-FR";
};

const enumKey = (value) =>
  String(value || "unknown")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase() || "unknown";

// Palette des « coins » — bords plus arrondis (rounded-2xl) et couleurs plus vives
// que l'Admin, pour une équipe de freelances plus jeune, tout en restant dans la
// même famille visuelle (traits fins, badge carré).
const COIN_ACCENTS = {
  amber: { bg: "bg-amber-50", border: "border-amber-100", icon: "text-amber-600" },
  rose: { bg: "bg-rose-50", border: "border-rose-100", icon: "text-rose-600" },
  blue: { bg: "bg-blue-50", border: "border-blue-100", icon: "text-blue-600" },
  violet: { bg: "bg-violet-50", border: "border-violet-100", icon: "text-violet-600" },
  green: { bg: "bg-emerald-50", border: "border-emerald-100", icon: "text-emerald-600" },
  slate: { bg: "bg-slate-100", border: "border-slate-200", icon: "text-slate-600" },
};

export default function TeamDashboard({ view = "hub" }) {
  const { t, i18n } = useTranslation();
  const locale = localeFor(i18n.resolvedLanguage || i18n.language);
  const { user, profile, profileLoading } = useAuth();
  const myCoins = coinsForProfile(profile);
  const showHub = view === "hub";
  const showPartners = view === "partenaires";

  // ============== ÉTATS - ACTIVITÉS & KPI ==============
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    pending: 0,
    sent: 0,
    urgent: 0,
  });

  // ============== ÉTATS - DEMANDES ASSURANCE ==============
  const [insuranceLoading, setInsuranceLoading] = useState(false);
  const [insuranceRequests, setInsuranceRequests] = useState([]);
  const [insuranceStats, setInsuranceStats] = useState({
    total: 0,
    pending: 0,
    assigned: 0,
  });
  const [assigningInsurance, setAssigningInsurance] = useState({});

  // ============== ÉTATS - PARTENAIRES ==============
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partners, setPartners] = useState([]);
  const [partnerActiveContracts, setPartnerActiveContracts] = useState({});
  const [partnerPendingCounts, setPartnerPendingCounts] = useState({});

  const [partnerDrawer, setPartnerDrawer] = useState(null);
  const [partnerContracts, setPartnerContracts] = useState([]);
  const [partnerEvents, setPartnerEvents] = useState([]);
  const [partnerDrawerLoading, setPartnerDrawerLoading] = useState(false);

  const [showContractModal, setShowContractModal] = useState(false);
  const [savingContract, setSavingContract] = useState(false);
  const [contractForm, setContractForm] = useState({
    partner_id: "",
    partner_type: "insurance",
    contract_type: "commission",
    commission_rate: "",
    fixed_amount: "",
    currency: "EUR",
    start_date: "",
    end_date: "",
    contract_document_url: "",
    notes_internal: "",
    is_active: true,
  });

  const [showEventModal, setShowEventModal] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [eventForm, setEventForm] = useState({
    partner_id: "",
    contract_id: "",
    event_type: "expertise",
    artwork_id: "",
    collection_id: "",
    client_id: "",
    reference_amount: "",
    currency: "EUR",
    occurred_at: "",
  });

  // ============== FONCTIONS UTILITAIRES ==============

  const translateEnum = (group, value, fallback = null) =>
    t(`teamDashboard.enums.${group}.${enumKey(value)}`, {
      defaultValue:
        fallback ||
        String(value || "—")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    });

  const formatNumber = (value) => Number(value || 0).toLocaleString(locale);
  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(locale) : "—";
  const formatDateTime = (value) =>
    value ? new Date(value).toLocaleString(locale) : "—";

  const daysSince = (iso) =>
    Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));

  const isoDateToday = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const badgeFor = (createdAt, status) => {
    if (status === "sent") {
      return {
        bg: "bg-green-100",
        text: "text-green-700",
        label: t("teamDashboard.activity.badges.sent"),
      };
    }
    const days = daysSince(createdAt);
    if (days >= 7) {
      return {
        bg: "bg-red-100",
        text: "text-red-700",
        label: t("teamDashboard.activity.badges.urgent"),
      };
    }
    if (days >= 5) {
      return {
        bg: "bg-orange-100",
        text: "text-orange-700",
        label: t("teamDashboard.activity.badges.toProcess"),
      };
    }
    if (days >= 2) {
      return {
        bg: "bg-yellow-100",
        text: "text-yellow-700",
        label: t("teamDashboard.activity.badges.pending"),
      };
    }
    return {
      bg: "bg-blue-100",
      text: "text-blue-700",
      label: t("teamDashboard.activity.badges.new"),
    };
  };

  const demoRows = useMemo(
    () => [
      {
        id: "demo-1",
        kind: "partner_request",
        title: t("teamDashboard.activity.demo.basquiat"),
        status: "pending",
        created_at: new Date(Date.now() - 2 * 86400000).toISOString(),
        actor: t("teamDashboard.activity.clientNamed", { name: "M. Martin" }),
        meta: "Centre Pompidou",
      },
      {
        id: "demo-2",
        kind: "partner_request",
        title: t("teamDashboard.activity.demo.soulages"),
        status: "sent",
        created_at: new Date(Date.now() - 6 * 86400000).toISOString(),
        actor: t("teamDashboard.activity.clientNamed", { name: "Mme Dupont" }),
        meta: "Christie's Paris",
        sent_at: new Date(Date.now() - 1 * 86400000).toISOString(),
      },
      {
        id: "demo-3",
        kind: "support_ticket",
        title: t("teamDashboard.activity.demo.insuranceQuestion"),
        status: "pending",
        created_at: new Date(Date.now() - 8 * 86400000).toISOString(),
        actor: t("teamDashboard.activity.clientNamed", { name: "S. Bernard" }),
        meta: t("teamDashboard.activity.channelNamed", { channel: "email" }),
      },
    ],
    [t]
  );

  // ============== CHARGEMENT DES DONNÉES ==============

  const loadInsuranceRequests = async () => {
    if (!user) return;
    setInsuranceLoading(true);
    try {
      const { data, error } = await supabase
        .from("insurance_requests")
        .select(
          "id,status,created_at,updated_at,created_by,artwork_id,partner_id,request_type,insured_value,currency,coverage_context,policy_kind,transport_needed,event_mode,event_start_date,event_end_date,storage_location,payload,seen_at,answered_at,closed_at,notes,partner_message,quote_amount,quote_currency"
        )
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      const rows = data || [];
      setInsuranceRequests(rows);

      const total = rows.length;
      const pending = rows.filter((r) => (r.status || "pending") === "pending").length;
      const assigned = rows.filter((r) => !!r.partner_id).length;
      setInsuranceStats({ total, pending, assigned });
    } catch (e) {
      console.warn("[TeamDashboard] loadInsuranceRequests error:", e);
      setInsuranceRequests([]);
      setInsuranceStats({ total: 0, pending: 0, assigned: 0 });
    } finally {
      setInsuranceLoading(false);
    }
  };

  const assignInsuranceRequestToPartner = async (requestId, partnerId) => {
    if (!requestId || !partnerId) return;
    setAssigningInsurance((s) => ({ ...s, [requestId]: true }));
    try {
      const { error } = await supabase
        .from("insurance_requests")
        .update({
          partner_id: partnerId,
          status: "pending",
        })
        .eq("id", requestId);

      if (error) throw error;
      await loadInsuranceRequests();
    } catch (e) {
      console.error("[TeamDashboard] assignInsuranceRequestToPartner error:", e);
      alert(e?.message || t("teamDashboard.insurance.assignmentError"));
    } finally {
      setAssigningInsurance((s) => ({ ...s, [requestId]: false }));
    }
  };

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let prData = [];
      try {
        const { data } = await supabase
          .from("partner_requests")
          .select(
            "id, status, created_at, sent_at, client_id, artwork_id, proposal_id, type, payload, taken_at, done_at"
          )
          .order("created_at", { ascending: false })
          .limit(25);
        prData = data || [];
      } catch (_) {}

      let irData = [];
      try {
        const { data } = await supabase
          .from("insurance_requests")
          .select(
            "id,status,created_at,created_by,artwork_id,partner_id,coverage_context,policy_kind,insured_value,currency,payload"
          )
          .order("created_at", { ascending: false })
          .limit(25);
        irData = data || [];
      } catch (_) {}

      let stData = [];
      try {
        const { data } = await supabase
          .from("v_support_tickets_dashboard")
          .select("id, status, created_at, channel, subject, client_id")
          .order("created_at", { ascending: false })
          .limit(25);

        stData = (data || []).map((row) => ({
          id: row.id,
          kind: "support_ticket",
          title: row.subject || t("teamDashboard.activity.supportTicket"),
          status: row.status || "pending",
          created_at: row.created_at,
          actor: t("teamDashboard.activity.clientNumber", { id: row.client_id }),
          meta: t("teamDashboard.activity.channelNamed", {
            channel: row.channel || t("teamDashboard.common.notAvailable"),
          }),
        }));
      } catch (_) {}

      const prActivities = prData.map((row) => {
        const payload = row.payload || {};
        const humanType = translateEnum(
          "requestTypes",
          row.type || "general"
        );
        const institution =
          payload.institution ||
          payload.museum ||
          payload.auction_house ||
          payload.destination ||
          null;
        const subject =
          payload.title || payload.subject || payload.description || null;

        const titleParts = [];
        titleParts.push(humanType);
        if (subject) titleParts.push(subject);
        if (institution) titleParts.push(institution);

        return {
          id: row.id,
          kind: "partner_request",
          title: titleParts.filter(Boolean).join(" — ") || `partner_request — ${row.id}`,
          status: row.status || "pending",
          created_at: row.created_at,
          sent_at: row.sent_at,
          actor: t("teamDashboard.activity.clientNumber", { id: row.client_id }),
          meta: row.artwork_id
            ? t("teamDashboard.activity.artworkNumber", { id: row.artwork_id })
            : row.proposal_id
            ? t("teamDashboard.activity.proposalNumber", { id: row.proposal_id })
            : "—",
        };
      });

      const irActivities = (irData || []).map((row) => {
        const payload = row.payload || {};
        const clientName = payload?.client?.name || payload?.client_name || null;
        const context = translateEnum(
          "insuranceContexts",
          row.coverage_context || "insurance"
        );
        const policyKind = row.policy_kind
          ? ` • ${translateEnum("policyKinds", row.policy_kind)}`
          : "";
        const insuredValue =
          row.insured_value !== null && row.insured_value !== undefined
            ? ` • ${formatNumber(row.insured_value)} ${row.currency || "EUR"}`
            : "";

        return {
          id: row.id,
          kind: "insurance_request",
          title: t("teamDashboard.activity.insuranceTitle", {
            context,
            policyKind,
            insuredValue,
          }).trim(),
          status: row.status || "pending",
          created_at: row.created_at,
          actor: clientName
            ? t("teamDashboard.activity.clientNamed", { name: clientName })
            : t("teamDashboard.activity.clientNumber", { id: row.created_by }),
          meta: row.artwork_id
            ? t("teamDashboard.activity.artworkNumber", { id: row.artwork_id })
            : "—",
        };
      });

      let kpis = { total: 0, pending: 0, sent: 0, urgent: 0 };
      try {
        const { data: kp, error: kpErr } = await supabase
          .from("partner_requests")
          .select("id,status,created_at");

        if (kpErr) throw kpErr;

        const arr = kp || [];
        kpis.total = arr.length;
        kpis.pending = arr.filter((r) => r.status === "pending").length;
        kpis.sent = arr.filter((r) => r.status === "sent").length;
        kpis.urgent = arr.filter(
          (r) => r.status === "pending" && daysSince(r.created_at) >= 5
        ).length;
      } catch (_) {}

      if (kpis.total === 0 && stData.length === 0) {
        const totalDemo = demoRows.filter((r) => r.kind === "partner_request").length;
        const pendingDemo = demoRows.filter(
          (r) => r.status === "pending" && r.kind === "partner_request"
        ).length;
        const sentDemo = demoRows.filter(
          (r) => r.status === "sent" && r.kind === "partner_request"
        ).length;
        const urgentDemo = demoRows.filter(
          (r) =>
            r.kind === "partner_request" &&
            r.status === "pending" &&
            daysSince(r.created_at) >= 5
        ).length;

        setStats({
          totalRequests: totalDemo,
          pending: pendingDemo,
          sent: sentDemo,
          urgent: urgentDemo,
        });
        setActivities(demoRows);
      } else {
        setStats({
          totalRequests: kpis.total,
          pending: kpis.pending,
          sent: kpis.sent,
          urgent: kpis.urgent,
        });
        setActivities([...irActivities, ...prActivities, ...stData]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPartnersOverview = async () => {
    if (!user) return;
    setPartnersLoading(true);
    try {
      const { data: pData, error: pErr } = await supabase.rpc("get_partner_directory");
      if (pErr) {
        console.warn("[TeamDashboard] get_partner_directory RPC error:", pErr);
        setPartners([]);
      } else {
        setPartners(pData || []);
      }

      const today = isoDateToday();
      const { data: cData, error: cErr } = await supabase
        .from("partner_contracts")
        .select("id,partner_id,partner_type,contract_type,start_date,end_date,is_active")
        .eq("is_active", true);

      if (cErr) throw cErr;

      const activeMap = {};
      (cData || []).forEach((c) => {
        const okStart = !c.start_date || c.start_date <= today;
        const okEnd = !c.end_date || c.end_date >= today;
        if (!okStart || !okEnd) return;
        if (!activeMap[c.partner_id]) activeMap[c.partner_id] = c;
      });
      setPartnerActiveContracts(activeMap);

      const { data: eData, error: eErr } = await supabase
        .from("partner_billable_events")
        .select("partner_id,status")
        .eq("status", "pending");

      if (eErr) throw eErr;

      const counts = {};
      (eData || []).forEach((row) => {
        counts[row.partner_id] = (counts[row.partner_id] || 0) + 1;
      });
      setPartnerPendingCounts(counts);
    } catch (e) {
      console.error(e);
      setPartners([]);
      setPartnerActiveContracts({});
      setPartnerPendingCounts({});
    } finally {
      setPartnersLoading(false);
    }
  };

  const openPartnerDrawer = async (partner) => {
    if (!partner?.id) return;
    setPartnerDrawer({ id: partner.id, email: partner.email || "" });
    setPartnerDrawerLoading(true);
    try {
      const { data: cData, error: cErr } = await supabase
        .from("partner_contracts")
        .select(
          "id,partner_id,partner_type,contract_type,commission_rate,fixed_amount,currency,start_date,end_date,contract_document_url,notes_internal,is_active,created_at,created_by"
        )
        .eq("partner_id", partner.id)
        .order("created_at", { ascending: false });
      if (cErr) throw cErr;
      setPartnerContracts(cData || []);

      const { data: eData, error: eErr } = await supabase
        .from("partner_billable_events")
        .select(
          "id,partner_id,contract_id,event_type,artwork_id,collection_id,client_id,reference_amount,currency,status,occurred_at,created_at"
        )
        .eq("partner_id", partner.id)
        .order("occurred_at", { ascending: false })
        .limit(25);
      if (eErr) throw eErr;
      setPartnerEvents(eData || []);
    } catch (e) {
      console.error(e);
      setPartnerContracts([]);
      setPartnerEvents([]);
    } finally {
      setPartnerDrawerLoading(false);
    }
  };

  const closePartnerDrawer = () => {
    setPartnerDrawer(null);
    setPartnerContracts([]);
    setPartnerEvents([]);
  };

  const openCreateContract = (partnerId) => {
    setContractForm((s) => ({
      ...s,
      partner_id: partnerId || "",
      start_date: s.start_date || isoDateToday(),
    }));
    setShowContractModal(true);
  };

  const openCreateEvent = (partnerId) => {
    const active =
      partnerContracts.find((c) => c.is_active) ||
      partnerActiveContracts[partnerId] ||
      null;

    setEventForm((s) => ({
      ...s,
      partner_id: partnerId || "",
      contract_id: active?.id || "",
      occurred_at: s.occurred_at || new Date().toISOString().slice(0, 16),
    }));
    setShowEventModal(true);
  };

  async function savePartnerContract() {
    try {
      if (!contractForm.partner_id) {
        window.alert(t("teamDashboard.validation.partnerRequired"));
        return;
      }
      if (!contractForm.start_date) {
        window.alert(t("teamDashboard.validation.startDateRequired"));
        return;
      }
      setSavingContract(true);

      const payload = {
        partner_id: contractForm.partner_id,
        partner_type: contractForm.partner_type,
        contract_type: contractForm.contract_type,
        commission_rate:
          contractForm.contract_type === "commission" &&
          contractForm.commission_rate !== ""
            ? Number(String(contractForm.commission_rate).replace(",", "."))
            : null,
        fixed_amount:
          (contractForm.contract_type === "reverse" ||
            contractForm.contract_type === "forfait") &&
          contractForm.fixed_amount !== ""
            ? Number(String(contractForm.fixed_amount).replace(",", "."))
            : null,
        currency: contractForm.currency || "EUR",
        start_date: contractForm.start_date,
        end_date: contractForm.end_date || null,
        contract_document_url: contractForm.contract_document_url?.trim() || null,
        notes_internal: contractForm.notes_internal?.trim() || null,
        is_active: !!contractForm.is_active,
        created_by: user?.id || null,
      };

      const { error } = await supabase.from("partner_contracts").insert([payload]);
      if (error) throw error;

      setShowContractModal(false);
      if (partnerDrawer?.id) {
        await openPartnerDrawer({ id: partnerDrawer.id, email: partnerDrawer.email });
      }
      await loadPartnersOverview();
    } catch (e) {
      console.error(e);
      window.alert(t("teamDashboard.contractModal.createError", { message: e.message }));
    } finally {
      setSavingContract(false);
    }
  }

  async function savePartnerEvent() {
    try {
      if (!eventForm.partner_id) {
        window.alert(t("teamDashboard.validation.partnerRequired"));
        return;
      }
      if (!eventForm.contract_id) {
        window.alert(t("teamDashboard.validation.contractRequired"));
        return;
      }
      if (!eventForm.occurred_at) {
        window.alert(t("teamDashboard.validation.occurredAtRequired"));
        return;
      }
      setSavingEvent(true);

      const payload = {
        partner_id: eventForm.partner_id,
        contract_id: eventForm.contract_id,
        event_type: eventForm.event_type,
        artwork_id: eventForm.artwork_id?.trim() || null,
        collection_id: eventForm.collection_id?.trim() || null,
        client_id: eventForm.client_id?.trim() || null,
        reference_amount:
          eventForm.reference_amount !== "" ? Number(eventForm.reference_amount) : null,
        currency: eventForm.currency || "EUR",
        occurred_at: new Date(eventForm.occurred_at).toISOString(),
        status: "pending",
      };

      const { error } = await supabase.from("partner_billable_events").insert([payload]);
      if (error) throw error;

      setShowEventModal(false);
      if (partnerDrawer?.id) {
        await openPartnerDrawer({ id: partnerDrawer.id, email: partnerDrawer.email });
      }
      await loadPartnersOverview();
    } catch (e) {
      console.error(e);
      window.alert(t("teamDashboard.eventModal.createError", { message: e.message }));
    } finally {
      setSavingEvent(false);
    }
  }

  // ============== EFFETS DE BORD ==============

  useEffect(() => {
    loadData();
    loadPartnersOverview();
    loadInsuranceRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ============== FILTRAGE DES ACTIVITÉS ==============

  const filtered = activities.filter((a) => {
    if (filter === "all") return true;
    if (filter === "pending") return a.status === "pending";
    if (filter === "sent") return a.status === "sent";
    if (filter === "urgent") return a.status === "pending" && daysSince(a.created_at) >= 5;
    return true;
  });

  const handleActivityPrimaryAction = (item) => {
    if (item.kind === "support_ticket") {
      window.location.href = `/support/tickets/${item.id}`;
      return;
    }
  };

  const handleActivityToolAction = (item) => {
    if (item.kind === "support_ticket") {
      window.location.href = `/support/tickets/${item.id}`;
      return;
    }
    if (item.kind === "insurance_request") {
      window.location.href = `/insurance/requests/${item.id}`;
    }
  };

  // ============== RENDER ==============

  if (profileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f9f7f4]">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-gray-900" />
      </div>
    );
  }
  if (showPartners && !hasCoin(profile, "partenaires")) {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <div className="text-4xl" aria-hidden>🔒</div>
        <h1 className="mt-4 font-serif text-2xl font-semibold text-gray-900">{t("teamDashboard.accessDenied.title")}</h1>
        <p className="mt-2 text-sm text-gray-600">{t("teamDashboard.accessDenied.description")}</p>
        <Link to="/team/dashboard" className="mt-6 inline-block rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800">{t("teamDashboard.accessDenied.back")}</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9f7f4]">
      <div className="max-w-7xl mx-auto px-6 py-10">

        {/* ── BANDEAU PILOTAGE ─────────────────────────────────────────── */}
        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            {showPartners && (
              <Link to="/team/dashboard" className="mb-2 inline-block text-sm font-medium text-gray-500 hover:text-gray-800">← {t("teamDashboard.header.mySpace")}</Link>
            )}
            <h1 className="text-4xl font-light tracking-tight text-gray-900">
              {showPartners ? t("teamDashboard.header.partnersTitle") : t("teamDashboard.header.hubTitle")}
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              {showPartners
                ? t("teamDashboard.header.partnersSubtitle")
                : t("teamDashboard.header.hubSubtitle")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <button
              onClick={async () => {
                await loadData();
                await loadPartnersOverview();
                await loadInsuranceRequests();
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition"
            >
              <RefreshCw className="w-4 h-4" />
              {t("teamDashboard.actions.refresh")}
            </button>
            <a
              href="/search"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition"
            >
              <Search className="w-4 h-4" />
              {t("teamDashboard.actions.search")}
            </a>
            <a
              href="/team/import"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1a1a1a] text-white hover:bg-gray-800 transition"
              title={t("teamDashboard.actions.importClientTitle")}
            >
              {t("teamDashboard.actions.importClient")}
            </a>
            <a
              href="/team/auction-houses"
              className="px-4 py-2 rounded-xl bg-[#1a1a1a] text-white hover:bg-gray-800 transition"
            >
              {t("teamDashboard.actions.manageAuctionHouses")}
            </a>
          </div>
        </div>

        {/* ── VOS ESPACES (COINS) ─────────────────────────────────────── */}
        <div className={`mb-12 ${showHub ? "" : "hidden"}`}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">{t("teamDashboard.hub.spacesTitle")}</h2>
              <p className="text-sm text-gray-500">{t("teamDashboard.hub.spacesSubtitle")}</p>
            </div>
            {profile?.role === "admin" && (
              <Link to="/admin/team-access" className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                {t("teamDashboard.hub.manageTeamAccess")}
              </Link>
            )}
          </div>
          {myCoins.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
              {t("teamDashboard.hub.noSpaces")}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {myCoins.map((key) => {
                const c = COIN_BY_KEY[key];
                if (!c) return null;
                const Icon = c.icon;
                const tone = COIN_ACCENTS[c.accent] || COIN_ACCENTS.slate;
                const inner = (
                  <>
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${tone.bg} ${tone.border}`} aria-hidden>
                      {Icon && <Icon className={`h-5 w-5 ${tone.icon}`} strokeWidth={1.75} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-gray-900">
                        {t(`teamDashboard.coins.${key}.title`, { defaultValue: c.title })}
                        {!c.ready && <span className="ml-1 text-[10px] text-gray-400">({t("teamDashboard.hub.comingSoon")})</span>}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500">{t(`teamDashboard.coins.${key}.description`, { defaultValue: c.desc })}</span>
                    </span>
                  </>
                );
                return c.ready ? (
                  <Link key={key} to={c.route} className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                    {inner}
                  </Link>
                ) : (
                  <div key={key} className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 opacity-70">
                    {inner}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* KPI */}
        <div className={`grid grid-cols-2 md:grid-cols-5 gap-4 mb-14 ${showPartners ? "" : "hidden"}`}>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-3xl font-light text-gray-900">{stats.totalRequests}</div>
            <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">{t("teamDashboard.kpis.partnerRequests")}</div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-3xl font-light text-gray-900">{insuranceStats.pending}</div>
            <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">{t("teamDashboard.kpis.insurancePending")}</div>
          </div>
          <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-6 shadow-sm">
            <div className="text-3xl font-light text-yellow-800">{stats.pending}</div>
            <div className="text-xs text-yellow-600 mt-1 uppercase tracking-wider">{t("teamDashboard.kpis.pending")}</div>
          </div>
          <div className="rounded-2xl border border-green-200 bg-green-50 p-6 shadow-sm">
            <div className="text-3xl font-light text-green-800">{stats.sent}</div>
            <div className="text-xs text-green-600 mt-1 uppercase tracking-wider">{t("teamDashboard.kpis.sent")}</div>
          </div>
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 shadow-sm">
            <div className="text-3xl font-light text-red-800">{stats.urgent}</div>
            <div className="text-xs text-red-600 mt-1 uppercase tracking-wider">{t("teamDashboard.kpis.urgent")}</div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 1 — OPÉRATIONS IMMÉDIATES
        ══════════════════════════════════════════════════════════════════ */}
        <section className={`mb-14 ${showPartners ? "" : "hidden"}`}>
          <div className="mb-8">
            <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-2">
              {t("teamDashboard.operations.eyebrow")}
            </p>
            <h2 className="text-2xl font-light text-gray-900">
              {t("teamDashboard.operations.title")}
            </h2>
          </div>

          <div className="space-y-12">

            {/* ── Activité récente ── */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-light text-gray-900">{t("teamDashboard.activity.title")}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t("teamDashboard.activity.subtitle")}
                  </p>
                </div>
                {/* Filtres repositionnés en haut à droite */}
                <div className="flex gap-1">
                  {[
                    { key: "all", label: t("teamDashboard.activity.filters.all") },
                    { key: "pending", label: t("teamDashboard.activity.filters.pending") },
                    { key: "sent", label: t("teamDashboard.activity.filters.sent") },
                    { key: "urgent", label: t("teamDashboard.activity.filters.urgent") },
                  ].map((btn) => (
                    <button
                      key={btn.key}
                      onClick={() => setFilter(btn.key)}
                      className={`px-3 py-1.5 text-xs rounded-lg font-medium transition ${
                        filter === btn.key
                          ? "bg-[#1a1a1a] text-white"
                          : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Zone chaude encapsulée dans une card */}
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
                {loading ? (
                  <div className="text-sm text-gray-500">{t("teamDashboard.common.loading")}</div>
                ) : (
                  <>
                    {filtered.map((item) => {
                      const badge = badgeFor(item.created_at, item.status);
                      return (
                        <div
                          key={item.id}
                          className="border border-gray-100 rounded-2xl p-5 hover:border-gray-200 hover:shadow-sm transition"
                        >
                          <div className="flex items-start justify-between gap-6">
                            <div className="flex items-start gap-4 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                                {item.kind === "partner_request" ? (
                                  <ClipboardList className="w-5 h-5 text-gray-700" />
                                ) : (
                                  <Mail className="w-5 h-5 text-gray-700" />
                                )}
                              </div>

                              <div className="min-w-0">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <h3 className="text-base font-medium text-gray-900 truncate">
                                    {item.title}
                                  </h3>
                                  <span
                                    className={`px-2 py-0.5 text-xs rounded-full ${badge.bg} ${badge.text}`}
                                  >
                                    {badge.label}
                                  </span>
                                </div>

                                <div className="text-sm text-gray-600 mt-1">
                                  {t("teamDashboard.activity.createdAgo", { actor: item.actor, count: daysSince(item.created_at) })}
                                </div>

                                {item.meta ? (
                                  <div className="text-sm text-gray-500 mt-1">{item.meta}</div>
                                ) : null}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {item.kind === "support_ticket" ? (
                                <a
                                  href={`/support/tickets/${item.id}`}
                                  className={`px-4 py-2 rounded-xl text-sm font-medium ${
                                    item.status === "pending"
                                      ? "bg-[#1a1a1a] text-white hover:bg-gray-800"
                                      : "bg-gray-100 hover:bg-gray-200"
                                  }`}
                                >
                                  {item.status === "pending" ? t("teamDashboard.actions.open") : t("teamDashboard.actions.details")}
                                </a>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleActivityPrimaryAction(item)}
                                  className={`px-4 py-2 rounded-xl text-sm font-medium ${
                                    item.status === "pending"
                                      ? "bg-[#1a1a1a] text-white hover:bg-gray-800"
                                      : "bg-gray-100 hover:bg-gray-200"
                                  }`}
                                >
                                  {t("teamDashboard.actions.details")}
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => handleActivityToolAction(item)}
                                className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                                title={t("teamDashboard.actions.viewInTool")}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {!loading && filtered.length === 0 && (
                      <div className="text-sm text-gray-500">{t("teamDashboard.activity.empty")}</div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Demandes assurance ── */}
            <div>
              <div className="flex items-end justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-light text-gray-900">{t("teamDashboard.insurance.title")}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t("teamDashboard.insurance.subtitle")}
                  </p>
                </div>
                <button
                  onClick={loadInsuranceRequests}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t("teamDashboard.actions.refresh")}
                </button>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                {insuranceLoading ? (
                  <div className="p-6 text-sm text-gray-500">
                    {t("teamDashboard.insurance.loading")}
                  </div>
                ) : insuranceRequests.length === 0 ? (
                  <div className="p-6 text-sm text-gray-500">
                    {t("teamDashboard.insurance.empty")}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.insurance.table.date")}</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.insurance.table.client")}</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.insurance.table.context")}</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.insurance.table.value")}</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.insurance.table.artwork")}</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.insurance.table.status")}</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.insurance.table.partner")}</th>
                          <th className="text-right text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.insurance.table.action")}</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-50">
                        {(insuranceRequests || []).map((r) => {
                          const payload = r.payload || {};
                          const clientName = payload?.client?.name || payload?.client_name || null;
                          const clientLabel = clientName ? clientName : `#${r.created_by}`;
                          const context = r.coverage_context
                            ? translateEnum("insuranceContexts", r.coverage_context)
                            : "—";
                          const policyKind = r.policy_kind
                            ? ` / ${translateEnum("policyKinds", r.policy_kind)}`
                            : "";
                          const insuredValue =
                            r.insured_value !== null && r.insured_value !== undefined
                              ? `${formatNumber(r.insured_value)} ${r.currency || "EUR"}`
                              : "—";

                          return (
                            <tr key={r.id} className="hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                {formatDate(r.created_at)}
                              </td>
                              <td className="px-4 py-3 text-gray-900 whitespace-nowrap font-medium">
                                {clientLabel}
                              </td>
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                {context}
                                {policyKind}
                                {r.transport_needed ? (
                                  <span className="text-gray-500"> • {t("teamDashboard.enums.eventTypes.transport")}</span>
                                ) : null}
                              </td>
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                {insuredValue}
                              </td>
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                {r.artwork_id ? (
                                  <span className="font-mono text-xs">{r.artwork_id}</span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                {translateEnum("statuses", r.status || "pending")}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                <select
                                  className="rounded-xl border border-gray-200 p-2 bg-white"
                                  value={r.partner_id || ""}
                                  onChange={(e) =>
                                    assignInsuranceRequestToPartner(r.id, e.target.value)
                                  }
                                  disabled={!!assigningInsurance[r.id]}
                                >
                                  <option value="">{t("teamDashboard.insurance.unassigned")}</option>
                                  {(partners || []).map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.email ? p.email : p.id}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <a
                                  href={`/insurance/requests/${r.id}`}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#1a1a1a] text-white hover:bg-gray-800 transition"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  {t("teamDashboard.actions.open")}
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 2 — WORKFLOWS MÉTIER
        ══════════════════════════════════════════════════════════════════ */}
        {showPartners && (
        <section className="mb-14 border-t border-gray-200 pt-12">
          <div className="mb-8">
            <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-2">
              {t("teamDashboard.workflows.eyebrow")}
            </p>
            <h2 className="text-2xl font-light text-gray-900">
              {t("teamDashboard.workflows.title")}
            </h2>
          </div>

          <div className="space-y-12">

            {/* ── Services curatoriaux ── */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-light text-gray-900">{t("teamDashboard.curatorial.title")}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t("teamDashboard.curatorial.subtitle")}
                  </p>
                </div>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t("teamDashboard.actions.refresh")}
                </button>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <CuratorialRequestsSection />
              </div>
            </div>

            {/* ── Demandes d'estimation par un expert ── */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-light text-gray-900">{t("teamDashboard.valuationExpert.title")}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t("teamDashboard.valuationExpert.subtitle")}
                  </p>
                </div>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t("teamDashboard.actions.refresh")}
                </button>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <ValuationRequestsSection />
              </div>
            </div>

            {/* ── Partenaires ── */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-light text-gray-900">{t("teamDashboard.partners.title")}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t("teamDashboard.partners.subtitle")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={loadPartnersOverview}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {t("teamDashboard.actions.refresh")}
                  </button>
                </div>
              </div>

              <div className="mb-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <div className="flex items-center gap-2 w-full md:max-w-md">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-gray-700" />
                  </div>
                  <input
                    className="w-full rounded-xl border border-gray-200 px-4 py-2 text-sm bg-white"
                    placeholder={t("teamDashboard.partners.searchPlaceholder")}
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                  />
                </div>
                <div className="text-xs text-gray-500">
                  {partnersLoading ? t("teamDashboard.common.loading") : t("teamDashboard.partners.count", { count: partners.length })}
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.partners.table.email")}</th>
                      <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.partners.table.specialty")}</th>
                      <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.partners.table.activeContract")}</th>
                      <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.partners.table.pendingFiles")}</th>
                      <th className="text-right text-[11px] font-medium text-gray-400 uppercase tracking-wider px-4 py-3">{t("teamDashboard.partners.table.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(partners || [])
                      .filter((p) => {
                        const q = (partnerSearch || "").trim().toLowerCase();
                        if (!q) return true;
                        return String(p.email || "")
                          .toLowerCase()
                          .includes(q);
                      })
                      .map((p) => {
                        const active = partnerActiveContracts[p.id] || null;
                        const pending = partnerPendingCounts[p.id] || 0;
                        return (
                          <tr key={p.id} className="border-t border-gray-50 hover:bg-gray-50 transition">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">
                                {p.email || `#${p.id}`}
                              </div>
                              <div className="text-xs text-gray-400 truncate font-mono">{p.id}</div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {active?.partner_type ? (
                                translateEnum("partnerTypes", active.partner_type)
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {active ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                  {t("teamDashboard.common.active")}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-500 text-xs">
                                  {t("teamDashboard.common.none")}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${pending > 0 ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>
                                {pending}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => openPartnerDrawer({ id: p.id, email: p.email })}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#1a1a1a] text-white hover:bg-gray-800 transition"
                              >
                                <ExternalLink className="w-4 h-4" />
                                {t("teamDashboard.actions.open")}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    {!partnersLoading && (partners || []).length === 0 && (
                      <tr>
                        <td className="px-4 py-6 text-gray-500" colSpan={5}>
                          {t("teamDashboard.partners.empty")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>


          </div>
        </section>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 3 — RESSOURCES
        ══════════════════════════════════════════════════════════════════ */}
        {showHub && (
        <section className="border-t border-gray-200 pt-12">
          <div className="mb-8">
            <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-2">
              {t("teamDashboard.resources.eyebrow")}
            </p>
            <h2 className="text-2xl font-light text-gray-900">
              {t("teamDashboard.resources.title")}
            </h2>
          </div>

          <div className="space-y-12">

            {/* ── Espace personnel ── */}
            <div>
              <h3 className="text-xl font-light text-gray-900 mb-6">{t("teamDashboard.personal.title")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="border border-gray-200 rounded-2xl p-6 bg-white hover:shadow-lg transition">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <UserCircle2 className="w-7 h-7 text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{t("teamDashboard.personal.profile")}</div>
                      <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                    </div>
                  </div>
                  <a
                    href="/profile"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium transition"
                  >
                    {t("teamDashboard.actions.open")} →
                  </a>
                </div>

                <div className="border border-gray-200 rounded-2xl p-6 bg-white hover:shadow-lg transition">
                  <div className="font-medium mb-4 flex items-center gap-2 text-gray-900">
                    <Search className="w-5 h-5 text-gray-500" />
                    {t("teamDashboard.personal.shortcuts")}
                  </div>
                  <div className="space-y-2">
                    <a
                      href="/requests"
                      className="block text-sm text-gray-700 hover:text-black hover:bg-gray-50 px-3 py-2 rounded-lg transition"
                    >
                      {t("teamDashboard.personal.allRequests")}
                    </a>
                    <a
                      href="/search"
                      className="block text-sm text-gray-700 hover:text-black hover:bg-gray-50 px-3 py-2 rounded-lg transition"
                    >
                      {t("teamDashboard.actions.search")}
                    </a>
                    <a
                      href="/help"
                      className="block text-sm text-gray-700 hover:text-black hover:bg-gray-50 px-3 py-2 rounded-lg transition"
                    >
                      {t("teamDashboard.personal.help")}
                    </a>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-2xl p-6 bg-white hover:shadow-lg transition">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <div className="font-medium text-gray-900">{t("teamDashboard.personal.systemStatus")}</div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">API</span>
                      <span className="text-green-700 font-medium">✓ {t("teamDashboard.personal.operational")}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("teamDashboard.personal.database")}</span>
                      <span className="text-green-700 font-medium">✓ {t("teamDashboard.personal.operational")}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Edge Functions</span>
                      <span className="text-green-700 font-medium">✓ {t("teamDashboard.personal.operational")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Collections & œuvres partagées ── */}
            <div>
              <div className="mb-6">
                <h3 className="text-xl font-light text-gray-900">{t("teamDashboard.shared.title")}</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {t("teamDashboard.shared.subtitle")}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <SharedDashboard />
              </div>
            </div>

          </div>
        </section>
        )}

      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DRAWER PARTENAIRE — inchangé
      ══════════════════════════════════════════════════════════════════ */}
      {partnerDrawer && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30" onClick={closePartnerDrawer} />
          <div className="relative ml-auto h-full w-full max-w-2xl bg-white shadow-xl p-6 overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="text-xs text-gray-500">{t("teamDashboard.drawer.partner")}</div>
                <h3 className="text-xl font-medium text-gray-900 truncate">
                  {partnerDrawer.email || partnerDrawer.id}
                </h3>
                <div className="text-xs text-gray-500 truncate">{partnerDrawer.id}</div>
              </div>
              <button
                onClick={closePartnerDrawer}
                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                aria-label={t("teamDashboard.actions.close")}
              >
                ✕
              </button>
            </div>

            <div className="flex gap-2 mb-6 flex-wrap">
              <button
                onClick={() => openCreateContract(partnerDrawer.id)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1a1a1a] text-white hover:bg-gray-800 transition"
              >
                <PlusCircle className="w-4 h-4" />
                {t("teamDashboard.drawer.newContract")}
              </button>
              <button
                onClick={() => openCreateEvent(partnerDrawer.id)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition"
              >
                <FileText className="w-4 h-4" />
                {t("teamDashboard.drawer.newEvent")}
              </button>
              <button
                onClick={() =>
                  openPartnerDrawer({ id: partnerDrawer.id, email: partnerDrawer.email })
                }
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition"
              >
                <RefreshCw className="w-4 h-4" />
                {t("teamDashboard.actions.refresh")}
              </button>
            </div>

            {partnerDrawerLoading && (
              <div className="text-sm text-gray-500">{t("teamDashboard.common.loading")}</div>
            )}

            <div className="mb-8">
              <h4 className="text-lg font-medium text-gray-900 mb-2">{t("teamDashboard.drawer.contracts")}</h4>
              {!partnerDrawerLoading && partnerContracts.length === 0 && (
                <div className="text-sm text-gray-500">
                  {t("teamDashboard.drawer.noContracts")}
                </div>
              )}
              <div className="space-y-3">
                {partnerContracts.map((c) => (
                  <div key={c.id} className="border border-gray-200 rounded-2xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          {translateEnum("partnerTypes", c.partner_type)} • {translateEnum("contractTypes", c.contract_type)}
                          {c.is_active ? (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">
                              {t("teamDashboard.common.active")}
                            </span>
                          ) : (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
                              {t("teamDashboard.common.inactive")}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(c.start_date)} → {c.end_date ? formatDate(c.end_date) : "∞"} •{" "}
                          {c.currency || "EUR"}
                        </div>
                        <div className="text-xs text-gray-600 mt-2">
                          {c.contract_type === "commission" ? (
                            <span>{t("teamDashboard.drawer.commissionRate", { value: c.commission_rate !== null && c.commission_rate !== undefined ? formatNumber(c.commission_rate) : "—" })}</span>
                          ) : (
                            <span>{t("teamDashboard.drawer.fixedAmount", { value: c.fixed_amount !== null && c.fixed_amount !== undefined ? formatNumber(c.fixed_amount) : "—" })}</span>
                          )}
                        </div>
                        {c.contract_document_url && (
                          <a
                            href={c.contract_document_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-xs text-gray-700 hover:text-black mt-2"
                          >
                            <ExternalLink className="w-3 h-3" />
                            {t("teamDashboard.drawer.contractDocument")}
                          </a>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        {c.created_at ? formatDateTime(c.created_at) : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-2">
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                {t("teamDashboard.drawer.billableEvents")}
              </h4>
              {!partnerDrawerLoading && partnerEvents.length === 0 && (
                <div className="text-sm text-gray-500">{t("teamDashboard.drawer.noEvents")}</div>
              )}
              <div className="space-y-3">
                {partnerEvents.map((e) => (
                  <div key={e.id} className="border border-gray-200 rounded-2xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {translateEnum("eventTypes", e.event_type)} • {translateEnum("statuses", e.status)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {t("teamDashboard.drawer.occurredAt")}: {" "}
                          {formatDateTime(e.occurred_at)}
                        </div>
                        <div className="text-xs text-gray-600 mt-2">
                          {t("teamDashboard.drawer.base")}: {e.reference_amount !== null && e.reference_amount !== undefined ? formatNumber(e.reference_amount) : "—"} {e.currency || "EUR"}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          {t("teamDashboard.drawer.linkedContract")}: <span className="font-mono">{e.contract_id}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {e.created_at ? formatDateTime(e.created_at) : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL CONTRAT — inchangé
      ══════════════════════════════════════════════════════════════════ */}
      {showContractModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowContractModal(false)}
          />
          <div
            className="relative z-10 w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium">{t("teamDashboard.contractModal.title")}</h3>
              <button
                onClick={() => setShowContractModal(false)}
                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                aria-label={t("teamDashboard.actions.close")}
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("teamDashboard.fields.partnerId")}
                </label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
                  value={contractForm.partner_id}
                  onChange={(e) =>
                    setContractForm((s) => ({ ...s, partner_id: e.target.value }))
                  }
                  placeholder="uuid"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.partnerType")}
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={contractForm.partner_type}
                    onChange={(e) =>
                      setContractForm((s) => ({ ...s, partner_type: e.target.value }))
                    }
                  >
                    <option value="insurance">{translateEnum("partnerTypes", "insurance")}</option>
                    <option value="transport">{translateEnum("partnerTypes", "transport")}</option>
                    <option value="valuation_expert">{translateEnum("partnerTypes", "valuation_expert")}</option>
                    <option value="conservation_expert">{translateEnum("partnerTypes", "conservation_expert")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.contractType")}
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={contractForm.contract_type}
                    onChange={(e) =>
                      setContractForm((s) => ({ ...s, contract_type: e.target.value }))
                    }
                  >
                    <option value="commission">{translateEnum("contractTypes", "commission")}</option>
                    <option value="reverse">{translateEnum("contractTypes", "reverse")}</option>
                    <option value="forfait">{translateEnum("contractTypes", "forfait")}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.startDate")}
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={contractForm.start_date}
                    onChange={(e) =>
                      setContractForm((s) => ({ ...s, start_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.endDateOptional")}
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={contractForm.end_date}
                    onChange={(e) =>
                      setContractForm((s) => ({ ...s, end_date: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.currency")}
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={contractForm.currency}
                    onChange={(e) =>
                      setContractForm((s) => ({ ...s, currency: e.target.value }))
                    }
                    placeholder="EUR"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <input
                    id="is_active"
                    type="checkbox"
                    checked={!!contractForm.is_active}
                    onChange={(e) =>
                      setContractForm((s) => ({ ...s, is_active: e.target.checked }))
                    }
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">
                    {t("teamDashboard.fields.active")}
                  </label>
                </div>
              </div>

              {contractForm.contract_type === "commission" ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.commissionRate")}
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={contractForm.commission_rate}
                    onChange={(e) =>
                      setContractForm((s) => ({
                        ...s,
                        commission_rate: e.target.value,
                      }))
                    }
                    placeholder={t("teamDashboard.contractModal.commissionPlaceholder")}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.fixedAmount")}
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={contractForm.fixed_amount}
                    onChange={(e) =>
                      setContractForm((s) => ({ ...s, fixed_amount: e.target.value }))
                    }
                    placeholder={t("teamDashboard.contractModal.amountPlaceholder")}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("teamDashboard.fields.contractDocumentOptional")}
                </label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={contractForm.contract_document_url}
                  onChange={(e) =>
                    setContractForm((s) => ({
                      ...s,
                      contract_document_url: e.target.value,
                    }))
                  }
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("teamDashboard.fields.internalNotesOptional")}
                </label>
                <textarea
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 h-24"
                  value={contractForm.notes_internal}
                  onChange={(e) =>
                    setContractForm((s) => ({ ...s, notes_internal: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50"
                onClick={() => setShowContractModal(false)}
              >
                {t("teamDashboard.actions.cancel")}
              </button>
              <button
                className="rounded-lg bg-[#1a1a1a] px-4 py-2 text-white hover:bg-gray-800 disabled:opacity-50"
                onClick={savePartnerContract}
                disabled={savingContract}
              >
                {savingContract ? t("teamDashboard.actions.saving") : t("teamDashboard.contractModal.create")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL ÉVÉNEMENT — inchangé
      ══════════════════════════════════════════════════════════════════ */}
      {showEventModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowEventModal(false)}
          />
          <div
            className="relative z-10 w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium">{t("teamDashboard.eventModal.title")}</h3>
              <button
                onClick={() => setShowEventModal(false)}
                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                aria-label={t("teamDashboard.actions.close")}
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("teamDashboard.fields.partnerId")}
                </label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
                  value={eventForm.partner_id}
                  onChange={(e) =>
                    setEventForm((s) => ({ ...s, partner_id: e.target.value }))
                  }
                  placeholder="uuid"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.contractId")}
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
                    value={eventForm.contract_id}
                    onChange={(e) =>
                      setEventForm((s) => ({ ...s, contract_id: e.target.value }))
                    }
                  >
                    <option value="">{t("teamDashboard.eventModal.selectContract")}</option>
                    {partnerContracts
                      .filter((c) => c.partner_id === eventForm.partner_id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.id}
                        </option>
                      ))}
                  </select>
                  <div className="text-xs text-gray-400 mt-1">
                    {t("teamDashboard.eventModal.noContract")}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.eventType")}
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={eventForm.event_type}
                    onChange={(e) =>
                      setEventForm((s) => ({ ...s, event_type: e.target.value }))
                    }
                  >
                    <option value="expertise">{translateEnum("eventTypes", "expertise")}</option>
                    <option value="transport">{translateEnum("eventTypes", "transport")}</option>
                    <option value="insurance_policy">{translateEnum("eventTypes", "insurance_policy")}</option>
                    <option value="restoration">{translateEnum("eventTypes", "restoration")}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.referenceAmount")}
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={eventForm.reference_amount}
                    onChange={(e) =>
                      setEventForm((s) => ({
                        ...s,
                        reference_amount: e.target.value,
                      }))
                    }
                    placeholder={t("teamDashboard.eventModal.amountPlaceholder")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.currency")}
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={eventForm.currency}
                    onChange={(e) =>
                      setEventForm((s) => ({ ...s, currency: e.target.value }))
                    }
                    placeholder="EUR"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("teamDashboard.fields.occurredAt")}
                </label>
                <input
                  type="datetime-local"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={eventForm.occurred_at}
                  onChange={(e) =>
                    setEventForm((s) => ({ ...s, occurred_at: e.target.value }))
                  }
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.artworkIdOptional")}
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
                    value={eventForm.artwork_id}
                    onChange={(e) =>
                      setEventForm((s) => ({ ...s, artwork_id: e.target.value }))
                    }
                    placeholder="uuid"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.collectionIdOptional")}
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
                    value={eventForm.collection_id}
                    onChange={(e) =>
                      setEventForm((s) => ({ ...s, collection_id: e.target.value }))
                    }
                    placeholder="uuid"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("teamDashboard.fields.clientIdOptional")}
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
                    value={eventForm.client_id}
                    onChange={(e) =>
                      setEventForm((s) => ({ ...s, client_id: e.target.value }))
                    }
                    placeholder="uuid"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50"
                onClick={() => setShowEventModal(false)}
              >
                {t("teamDashboard.actions.cancel")}
              </button>
              <button
                className="rounded-lg bg-[#1a1a1a] px-4 py-2 text-white hover:bg-gray-800 disabled:opacity-50"
                onClick={savePartnerEvent}
                disabled={savingEvent}
              >
                {savingEvent ? t("teamDashboard.actions.saving") : t("teamDashboard.eventModal.create")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}